/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';

import { Tag, TagType } from '@components/Tag/Tag';
import { DocumentStatus } from '@shared/constants/document';
import { SigningActionType } from '@shared/constants/signing';

export namespace DocumentStatusTag {
  export interface Props {
    indicatorType: TagType;
    indicatorText: string;
  }
}

export const getActiveSigningDocumentIndicatorText = (activeSignerName: string | undefined) =>
  `SIGNING - ${activeSignerName}`;

export const getTagAttributes = (
  documentStatus: DocumentStatus,
  previousCall: boolean,
  isActiveSigningDocument: boolean,
  signingActionType: SigningActionType | undefined,
  activeSignerName: string | undefined,
) => {
  if (documentStatus === DocumentStatus.SIGNED) {
    return {
      indicatorText: 'SIGNED',
      indicatorType: 'info' as const,
    };
  }

  if (documentStatus === DocumentStatus.COMPLETED) {
    return {
      indicatorText: 'COMPLETED' + (previousCall ? ' EARLIER' : ''),
      indicatorType: 'affirm' as const,
    };
  }

  if (documentStatus === DocumentStatus.VOIDED) {
    return {
      indicatorText: 'VOIDED' + (previousCall ? ' EARLIER' : ''),
      indicatorType: 'void' as const,
    };
  }

  if (documentStatus === DocumentStatus.PENDING_REVIEW) {
    return {
      indicatorText: 'UNDER REVIEW',
      indicatorType: 'info' as const,
    };
  }

  if (documentStatus === DocumentStatus.EDITING) {
    return {
      indicatorText: 'EDITING',
      indicatorType: 'info' as const,
    };
  }

  if (isActiveSigningDocument && signingActionType !== SigningActionType.WAIT_FOR_SIGNING_TO_START) {
    return {
      indicatorText: getActiveSigningDocumentIndicatorText(activeSignerName),
      indicatorType: 'info' as const,
    };
  }

  return null;
};

export const DocumentStatusTag: React.FC<DocumentStatusTag.Props> = ({ indicatorType, indicatorText }) => {
  return (
    <Tag data-it="Tag" type={indicatorType}>
      {indicatorText}
    </Tag>
  );
};
